import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { XIcon } from "lucide-react";
import { toast } from "react-toastify";
import {
  usePremisesStore,
  useRegionStore,
  useOrgUsersStore,
} from "../../../store/store";
import {
  useEmergencyCategoryStore,
  useEmergencySubcategoryStore,
} from "../../../store/masterDataStore";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import { useDropzone } from "react-dropzone";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import {
  createResourceReadiness,
  getResourceReadinessById,
  updateResourceReadiness,
} from "../../../service/api";

interface AddResourcesProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  mode?: "create" | "view" | "update";
  resourceId?: string;
}

interface Attachment {
  url: string;
  filename?: string;
  name?: string;
  originalName?: string;
  uploadedAt?: string;
}

const AddResources: React.FC<AddResourcesProps> = ({
  onClose,
  isOpen,
  onSuccess,
  mode = "create",
  resourceId,
}) => {
  const [formData, setFormData] = useState({
    resourceId: "",
    location: "",
    emergencyCategoryId: "",
    emergencySubcategoryId: "",
    regionId: "",
    premisesId: "",
    floor: "",
    room: "", 
    lastCheck: "",
    nextInspectionDue: "",
    status: "",
    responsiblePerson: "",
    notes: "",
  });

  const [files, setFiles] = useState<File[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>([]);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");
  const [users, setUsers] = useState<any[]>([]);
  const [maxFileSize] = useState<number>(10);

  // Document preview states
  const [fileurl, setFileUrl] = useState({
    url: "",
    name: "",
    type: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);

  const { getsignedurl } = S3TempUrl();

  const {
    uploadFileToS3,
    progress: uploadProgress,
    uploading,
    error: uploadError,
  } = useS3Upload();

  const { allPremises, fetchPremises } = usePremisesStore();
  const { regions, fetchRegions } = useRegionStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();
  const { emergencyCategory, fetchAllEmergencyCategory } = useEmergencyCategoryStore();
  const { emergencySubcategory, fetchAllEmergencySubcategory } = useEmergencySubcategoryStore();

  useEffect(() => {
    if (!orgUsersData.length) fetchOrgUsers();
  }, [fetchOrgUsers]);

  useEffect(() => {
    if (orgUsersData.length > 0) {
      setUsers(orgUsersData);
    }
  }, [orgUsersData]);

  // State for filtered premises and floor options
  const [filteredPremises, setFilteredPremises] = useState<any[]>([]);
  const [floorOptions, setFloorOptions] = useState<string[]>([]);
  // Status options
  const statusOptions = ["OK", "Needs Service", "Critical"];

  // Helper function to get file extension
  const getFileExtension = (filename: string): string => {
    return filename.split(".").pop()?.toLowerCase() || "";
  };

  // Helper function to check if file can be previewed in browser
  const canPreviewFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      "jpg",
      "jpeg",
      "png",
      "pdf",
      "txt",
      "doc",
      "docx",
      "ppt",
      "pptx",
    ].includes(ext);
  };

  // Function to handle file preview
  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (canPreviewFile(filename)) {
      try {
        setIsLoading(true);

        const s3Key = attachment.url?.split("/").pop();
        if (!s3Key) {
          throw new Error("Invalid file URL");
        }

        const fileurl = await getsignedurl(s3Key, getFileExtension(filename));
        const filobj = {
          url: fileurl,
          name: filename,
          type: getFileExtension(filename),
        };
        setFileUrl(filobj);
        setIsDocumentViewerOpen(true);
      } catch (err: any) {
        console.error("Error previewing file:", err);
        setErrorMessage("File not found or failed to load");
        toast.error("Failed to preview file");
      } finally {
        setIsLoading(false);
      }
    } else {
      toast.info(
        "Preview not available for this file type. Use download instead."
      );
    }
  };

  // Function to close document viewer
  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  // Function to remove existing attachment (for update mode)
  const handleRemoveExistingAttachment = (index: number) => {
    if (mode === "view") return;

    setExistingAttachments((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      return updated;
    });
  };

  // Filter premises based on selected region
  useEffect(() => {
    if (formData.regionId) {
      const premisesInRegion = allPremises.filter(
        (p) => p.region === formData.regionId
      );
      setFilteredPremises(premisesInRegion);

      if (
        formData.premisesId &&
        !premisesInRegion.some((p) => p._id === formData.premisesId)
      ) {
        setFormData((prev) => ({
          ...prev,
          premisesId: "",
          floor: "",
          room: "",
        }));
      }
    } else {
      setFilteredPremises(allPremises);
    }
  }, [formData.regionId, allPremises, formData.premisesId]);

  // Generate floor options when premises changes
  useEffect(() => {
    if (!formData.premisesId) {
      setFloorOptions([]);
      setFormData((prev) => ({ ...prev, floor: "", room: "" }));
      return;
    }

    const selectedPremise = allPremises.find(
      (p) => p._id === formData.premisesId
    );
    if (!selectedPremise?.floor) {
      setFloorOptions([]);
      setFormData((prev) => ({ ...prev, floor: "", room: "" }));
      return;
    }

    const floorsCount = Number(selectedPremise.floor);
    if (isNaN(floorsCount)) {
      setFloorOptions([]);
      setFormData((prev) => ({ ...prev, floor: "", room: "" }));
      return;
    }

    const floors = Array.from(
      { length: floorsCount },
      (_, i) => `Floor ${i + 1}`
    );
    setFloorOptions(floors);
  }, [formData.premisesId, allPremises]);

  useEffect(() => {
    if (isOpen) {
      fetchPremises();
      if (!regions.length) fetchRegions();
      if (!emergencyCategory.length) fetchAllEmergencyCategory();
      if (!emergencySubcategory.length) fetchAllEmergencySubcategory();

      // Lock body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    // Cleanup function to restore body scroll
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    if ((mode === "view" || mode === "update") && resourceId && isOpen) {
      fetchResourceData();
    }
  }, [mode, resourceId, isOpen]);

  useEffect(() => {
    if (mode === "create" && isOpen) {
      const newUuid = uuidv4();
      const formattedId = `RES-${newUuid.substring(0, 8).toUpperCase()}`;

      setFormData((prev) => ({
        ...prev,
        resourceId: formattedId,
      }));
    }
  }, [mode, isOpen]);

  const fetchResourceData = async () => {
    if (!resourceId) {
      toast.error("Resource ID is required");
      return;
    }

    setLoading(true);
    try {
      const response = await getResourceReadinessById(resourceId as string);

      const resource = response.data.data;

      setFormData({
        resourceId: resource.resourceId || "",
        location: resource.location || "",
        emergencyCategoryId: resource.emergencyCategoryId || "",
        emergencySubcategoryId: resource.emergencySubcategoryId || "",
        regionId: resource.regionId || "",
        premisesId: resource.premisesId || "",
        floor: resource.floor || "",
        room: resource.room || "",
        lastCheck: resource.lastCheck
          ? resource.lastCheck.split("T")[0]
          : "",
        nextInspectionDue: resource.nextInspectionDue
          ? resource.nextInspectionDue.split("T")[0]
          : "",
        status: resource.status || "",
        responsiblePerson: resource.responsiblePerson || "",
        notes: resource.notes || "",
      });

      if (resource.attachments && resource.attachments.length > 0) {
        const normalizedAttachments = resource.attachments.map(
          (attachment: any) => {
            if (typeof attachment === "string") {
              return {
                url: attachment,
                filename: attachment.split("/").pop() || "Unknown file",
                uploadedAt: new Date().toISOString(),
              };
            } else {
              return {
                url: attachment.url || attachment,
                filename:
                  attachment.filename ||
                  attachment.name ||
                  attachment.originalName ||
                  attachment.url?.split("/").pop() ||
                  "Unknown file",
                uploadedAt: attachment.uploadedAt || new Date().toISOString(),
              };
            }
          }
        );
        setExistingAttachments(normalizedAttachments);
      }
    } catch (error: any) {
      toast.error("Failed to fetch resource data");
      console.error("Error fetching resource data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    if (mode === "view") return;

    const { name, value } = e.target;

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      // Reset dependent fields when region or premises changes
      if (name === "regionId") {
        updated.premisesId = "";
        updated.floor = "";
        updated.room = "";
      } else if (name === "premisesId") {
        updated.floor = "";
        updated.room = "";
      } else if (name === "floor") {
        updated.room = "";
      }

      return updated;
    });
  };

  const handleFileChange = async (acceptedFiles: File[]) => {
    if (mode === "view") return;

    const maxTotalSize = maxFileSize * 1024 * 1024;
    const existingFiles = files || [];
    const existingTotalSize = existingFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );

    const newFilesTotalSize = acceptedFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );
    const totalSize = existingTotalSize + newFilesTotalSize;

    if (totalSize > maxTotalSize) {
      toast.error(`Total file size exceeds ${maxFileSize}MB limit.`);
      return;
    }

    const updatedFiles = [...existingFiles, ...acceptedFiles];
    handleFilesChange(updatedFiles);
  };

  const handleFilesChange = (newFiles: File[] | null) => {
    setFiles(newFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileChange,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    disabled: mode === "view" || submitting || uploading,
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (mode === "view") return;

    // Validation
    const missingFields: string[] = [];
    if (!formData.resourceId) missingFields.push("Resource ID");
    if (!formData.regionId) missingFields.push("Region");
    if (!formData.premisesId) missingFields.push("Premises");
    if (!formData.status) missingFields.push("Status");

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    // Date validation
    if (formData.lastCheck && formData.nextInspectionDue) {
      const lastCheckDate = new Date(formData.lastCheck);
      const nextInspectionDate = new Date(formData.nextInspectionDue);

      if (nextInspectionDate < lastCheckDate) {
        toast.error("Next Inspection Due must be after Last Check date");
        return;
      }
    }

    // Validate total file size
    if (files && files.length > 0) {
      const maxTotalSize = maxFileSize * 1024 * 1024;
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxTotalSize) {
        toast.error(
          `Total file size exceeds ${maxFileSize}MB limit. Please remove some files.`
        );
        setUploadErrorMessage(
          "Total file size exceeds 10MB limit. Please remove some files."
        );
        return;
      }
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    let uploadedFileObjects: Array<{
      url: string;
      name: string;
      uploadedAt: string;
    }> = [];
    try {
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            uploadedFileObjects.push({
              url: s3Url,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            });
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      let response;

      // Prepare existing attachments as file objects
      const existingAttachmentObjects = existingAttachments.map((a) => ({
        url: a.url || (typeof a === "string" ? a : ""),
        name:
          a.filename ||
          a.name ||
          a.originalName ||
          a.url?.split("/").pop() ||
          "Unknown file",
        uploadedAt: a.uploadedAt || new Date().toISOString(),
      }));

      const payload = {
        ...formData,
        attachments: [...existingAttachmentObjects, ...uploadedFileObjects],
      };

      if (mode === "update") {
        if (!resourceId) {
          toast.error("Resource ID is required for update");
          return;
        }
        response = await updateResourceReadiness(resourceId, payload);
      } else {
        response = await createResourceReadiness(payload);
      }

      const successMessage =
        mode === "update"
          ? "Resource updated successfully"
          : "Resource created successfully";
      toast.success(successMessage);

      if (onSuccess) onSuccess();
      handleClose();

      if (mode === "create") {
        setFormData({
          resourceId: "",
          location: "",
          emergencyCategoryId: "",
          emergencySubcategoryId: "",
          regionId: "",
          premisesId: "",
          floor: "",
          room: "",
          lastCheck: "",
          nextInspectionDue: "",
          status: "",
          responsiblePerson: "",
          notes: "",
        });
        setFiles(null);
        setExistingAttachments([]);
      }
    } catch (error) {
      const errorMessage =
        (error as any).response?.data?.message ||
        (mode === "update"
          ? "Failed to update resource"
          : "Failed to create resource");
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (mode === "create") {
      setFormData({
        resourceId: "",
        location: "",
        emergencyCategoryId: "",
        emergencySubcategoryId: "",
        regionId: "",
        premisesId: "",
        floor: "",
        room: "",
        lastCheck: "",
        nextInspectionDue: "",
        status: "",
        responsiblePerson: "",
        notes: "",
      });
      setFiles(null);
      setExistingAttachments([]);
    }
    onClose();
  };

  const isReadOnly = mode === "view";
  const isDisabled = mode === "view" || submitting || uploading;

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
        <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
          <div className="text-lg">Loading resource data...</div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 z-[10000] bg-black bg-opacity-75 flex items-center justify-center overflow-hidden"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-6xl max-h-[95vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">
            {mode === "create"
              ? "Add Resource"
              : mode === "view"
              ? "View Resource"
              : "Edit Resource"}
          </h2>

          <button
            className="text-gray-600 hover:text-gray-800"
            onClick={handleClose}
          >
            <XIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} id="resourceForm" className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Resource ID - Auto-generated, read-only */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Resource ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="resourceId"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6] bg-gray-100"
                readOnly
                disabled
                value={formData.resourceId}
              />
            </div>

            {/* Emergency Category */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Emergency Category
              </label>
              <select
                name="emergencyCategoryId"
                value={formData.emergencyCategoryId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
              >
                <option value="">Select Emergency Category</option>
                {emergencyCategory.map((category: any) => (
                  <option key={category._id} value={category._id}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
            </div>

            {/* Emergency Sub Category */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Emergency Sub Category
              </label>
              <select
                name="emergencySubcategoryId"
                value={formData.emergencySubcategoryId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled || !formData.emergencyCategoryId}
              >
                <option value="">Select Emergency Sub Category</option>
                {emergencySubcategory
                  .filter(
                    (sub: any) => sub.categoryId === formData.emergencyCategoryId
                  )
                  .map((subcategory: any) => (
                    <option key={subcategory._id} value={subcategory._id}>
                      {subcategory.subcategoryName}
                    </option>
                  ))}
              </select>
            </div>

            {/* Region */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Region <span className="text-red-500">*</span>
              </label>
              <select
                name="regionId"
                value={formData.regionId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
                required
              >
                <option value="">Select Region</option>
                {regions.map((region) => (
                  <option key={region._id} value={region._id}>
                    {region.regionName}
                  </option>
                ))}
              </select>
            </div>

            {/* Premises */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Premises <span className="text-red-500">*</span>
              </label>
              <select
                name="premisesId"
                value={formData.premisesId}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled || !formData.regionId}
                required
              >
                <option value="">Select Premises</option>
                {filteredPremises.map((premise) => (
                  <option key={premise._id} value={premise._id}>
                    {premise.premisesName}
                  </option>
                ))}
              </select>
            </div>

            {/* Floor */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Floor
              </label>
              <select
                name="floor"
                value={formData.floor}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled || !formData.premisesId}
              >
                <option value="">Select Floor</option>
                {floorOptions.map((floor) => (
                  <option key={floor} value={floor}>
                    {floor}
                  </option>
                ))}
              </select>
            </div>

            {/* Room */}
            <div>
              <label className="block mb-1 text-black font-medium">Room</label>
              <input
                type="text"
                name="room"
                value={formData.room}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
                placeholder="Enter room number/name"
              />
            </div>

            {/* Last Check */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Last Check Date
              </label>
              <input
                type="date"
                name="lastCheck"
                value={formData.lastCheck}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
              />
            </div>

            {/* Next Inspection Due */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Next Inspection Due
              </label>
              <input
                type="date"
                name="nextInspectionDue"
                value={formData.nextInspectionDue}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
              />
            </div>

            {/* Status */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Status <span className="text-red-500">*</span>
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
                required
              >
                <option value="">Select Status</option>
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Responsible Person */}
            <div>
              <label className="block mb-1 text-black font-medium">
                Responsible Person
              </label>
              <select
                name="responsiblePerson"
                value={formData.responsiblePerson}
                onChange={handleChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
                disabled={isDisabled}
              >
                <option value="">Select Responsible Person</option>
                {users.map((user) => (
                  <option
                    key={user._id}
                    value={`${user.firstName} ${user.lastName}`}
                  >
                    {user.firstName} {user.lastName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Notes - Full width */}
          <div className="mb-6">
            <label className="block mb-1 text-black font-medium">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#0fa9a6]"
              disabled={isDisabled}
              rows={3}
              placeholder="Enter any additional notes or remarks"
            />
          </div>

          {/* File Upload Section */}
          <div className="mb-6">
            <label className="block mb-2 text-black font-medium">
              Attachments (Inspection Reports, Certificates)
            </label>

            {/* Existing Attachments */}
            {existingAttachments.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-medium mb-2">Existing Files:</h4>
                <div className="space-y-2">
                  {existingAttachments.map((attachment, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded border"
                    >
                      <span className="text-sm truncate flex-1">
                        {attachment.filename ||
                          attachment.name ||
                          "Unknown file"}
                      </span>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handlePreviewFile(attachment)}
                          className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                          Preview
                        </button>
                        {mode !== "view" && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveExistingAttachment(index)
                            }
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* New File Upload */}
            {mode !== "view" && (
              <div
                {...getRootProps()}
                className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:border-[#0fa9a6]"
              >
                <input {...getInputProps()} />
                <p className="text-gray-600">
                  Drag & drop files here, or click to select files
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Supported: PDF, DOC, DOCX, JPG, PNG (Max {maxFileSize}MB
                  total)
                </p>
              </div>
            )}

            {/* Selected Files Preview */}
            {files && files.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium mb-2">Selected Files:</h4>
                <div className="space-y-2">
                  {files.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 bg-blue-50 rounded border border-blue-200"
                    >
                      <span className="text-sm truncate flex-1">
                        {file.name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {uploadErrorMessage && (
              <p className="text-red-500 text-sm mt-2">{uploadErrorMessage}</p>
            )}
          </div>

          
          
        </form>
        {/* Form Actions */}
        <div className="flex justify-end gap-3 p-4 border-t bg-slate-600">
            <button
              type="button"
              
              onClick={handleClose}
              className="px-6 py-2 rounded-full bg-[#8497B0] text-white hover:bg-teal-600 transition"
              disabled={submitting}
            >
              {mode === "view" ? "Close" : "Cancel"}
            </button>
            {mode !== "view" && (
              <button
                type="submit"
                form="resourceForm"
                className="px-6 py-2 rounded-full bg-[#0fa9a6] text-white hover:bg-[#0d8a87] disabled:bg-gray-400"
                disabled={submitting || uploading}
              >
                {submitting
                  ? "Saving..."
                  : mode === "update"
                  ? "Update Resource"
                  : "Create Resource"}
              </button>
            )}
          </div>
      </div>

      {/* Document Viewer Modal */}
      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[10000] bg-black bg-opacity-75 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-2xl w-[95vw] max-w-6xl max-h-[95vh] overflow-y-auto relative">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
              <h2 className="text-lg font-semibold">Document Preview</h2>
              <button
                className="text-gray-600 hover:text-gray-800"
                onClick={handleCloseDocumentViewer}
              >
                <XIcon className="w-6 h-6" />
              </button>
            </div>
            <div className="p-4">
              {isLoading ? (
                <div className="text-center py-8">Loading document...</div>
              ) : errorMessage ? (
                <div className="text-red-500 text-center py-8">{errorMessage}</div>
              ) : (
                <DocumentViewer
                  file={fileurl.url}
                  name={fileurl.name}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddResources;
